import { type ComputedRef, computed } from 'vue';

import { breakpoint, exactBreakpoint } from './breakpoints';
import type { BreakpointType } from '../typings/breakpointType';

/**
 * Zwraca propsa tylko zależnie od breakpointu
 *
 * @export
 * @template BreakpointValueType
 * @param {{
 *   [key in BreakpointType]?: BreakpointValueType;
 * }} property
 * @return {*}  {(ComputedRef<BreakpointValueType | undefined>)}
 */
export function BreakpointDepended<BreakpointValueType>(property: {
  [key in BreakpointType]?: BreakpointValueType;
}): ComputedRef<BreakpointValueType | undefined> {
  return computed(() => {
    if (property === undefined || property === null) {
      return;
    }

    // Zmapowana lista zwracanych wartości dla wszystkich breakpointów
    const resultMap = {
      xs: undefined,
      sm: undefined,
      md: undefined,
      lg: undefined,
      xl: undefined,
      xxl: undefined
    } as { [key in BreakpointType]?: BreakpointValueType };

    // Wartość dla danego breakpointu
    let breakpointMapValue;

    // Finalna wartość z mapy, wskazywana przez aktualnie aktywny breakpoint
    let result;

    /**
     * Założenia działania tej funkcji są następujące:
     * Funkcja przyjmuje obiekt zbudowany z pól odnoszących się do breakpointów np. {xs: X, md: X, xl: X}
     * Gdzie X to dowolna wartość, typu który obsługiwany jest przez props który używa obsługę definiowania zależności od breakpointu.
     * Funkcja powinna zwracać wartość (X) dla aktualnie aktywnego breakpointu ORAZ wszystkich wyższych breakpointów, chyba że wartość dla wyższego breakpointu jest nadpisana.
     *
     * Przykład:
     * podanie {md: 1, xl: 2}
     * powinno zwrócić, zależnie od szerokości ekranu odpowiednie wartości:
     * xs: undefined, sm: undefined, md: 1, lg: 1, xl: 2, xxl: 2
     *
     * Przykład 2:
     * podanie {lg: true}
     * powinno zwrócić, zależnie od szerokości ekranu odpowiednie wartośći:
     * xs: undefined, sm: undefined, md: undefined, lg: true, xl: true, xxl: true
     */

    for (const [key] of Object.entries(breakpoint.value) as [BreakpointType, any][]) {
      if (property[key]) {
        breakpointMapValue = property[key];
      }

      resultMap[key] = breakpointMapValue;
    }

    if (exactBreakpoint.value) {
      result = resultMap[exactBreakpoint.value];
    }

    return result;
  });
}
